/**
 * ModelingEdge — custom React Flow edge with artifact_name label.
 *
 * Structural edges stay grey at rest. When an edge is on the current
 * highlight path it goes teal (design-system accent), matching
 * ResearchEdge §6.2.
 */

import React, { memo, useContext } from 'react';
import { getSmoothStepPath } from 'reactflow';
import type { EdgeProps } from 'reactflow';
import { HighlightContext } from '../shared/HighlightContext';
import type { RunStatus } from '../../types/modeling';

// Edge stroke colors. Brand and structural tokens resolve via CSS variables;
// graph-vocabulary status colors (running/completed/failed) stay as literal hex
// per the R5 carve-out — these are intentional semantic anchors, not brand.
// Used inside style={{ stroke: ... }}; modern Chromium resolves var() in SVG
// presentation attributes via parent computed style.
const C_DEFAULT = 'var(--border-strong)';
const C_ACCENT  = 'var(--secondary)';     // teal highlight path — pinned to secondary so it stays warm regardless of where --accent points (matches .graph-node.is-active)

export interface ModelingEdgeData {
  artifactName: string;
  sourceRunStatus?: RunStatus;
}

/**
 * Edge-label visibility, mirroring the plan graph: a node's incident edges
 * reveal their artifact label on hover/select (`isOnPath`). The global
 * "Edge labels" toggle is an all-at-rest override — honoured only when nothing
 * is actively highlighted, so a selection still isolates to its own labels.
 */
export function shouldShowEdgeLabel(opts: {
  artifactName: string;
  isOnPath: boolean;
  globalVisible: boolean;
  highlightActive: boolean;
}): boolean {
  return !!opts.artifactName && (opts.isOnPath || (opts.globalVisible && !opts.highlightActive));
}

export function getModelingEdgePathPresentation(
  isOnPath: boolean,
): {
  strokeColor: string;
  strokeWidth: number;
  pathFilter?: string;
} {
  if (isOnPath) {
    return {
      strokeColor: C_ACCENT,
      strokeWidth: 2,
      pathFilter: `drop-shadow(0 0 4px color-mix(in srgb, ${C_ACCENT} 35%, transparent))`,
    };
  }

  return {
    strokeColor: C_DEFAULT,
    strokeWidth: 1.5,
  };
}

function ModelingEdgeComponent({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
}: EdgeProps<ModelingEdgeData>) {
  const [edgePath] = getSmoothStepPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    borderRadius: 8,
  });

  const highlight = useContext(HighlightContext);
  const isOnPath = highlight.active && highlight.edgeKeys.has(id);
  const isDimmed = highlight.active && !isOnPath;

  const { strokeColor, strokeWidth, pathFilter } = getModelingEdgePathPresentation(isOnPath);
  const opacity = isDimmed ? 0.15 : 1;

  // Artifact-name labels are rendered by ProcessDAG's viewport-synced overlay
  // (ProductionEdgeLabels) on hover/select — mirroring the plan graph — not
  // per-edge here, which did not surface reliably in the locked grid (owner #3).
  return (
    <g style={{ opacity, transition: 'opacity 0.3s ease' }}>
      <path
        id={id}
        className="react-flow__edge-path"
        d={edgePath}
        style={{
          stroke: strokeColor,
          strokeWidth,
          fill: 'none',
          filter: pathFilter,
        }}
      />
    </g>
  );
}

export default memo(ModelingEdgeComponent);
